/* ============================================================
   Grill.jsx — kiosken räknar in burgarna som läggs på grillen;
   varje Hamburgarmål som säljs i kassan plockar av en.
   ============================================================ */

import { useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { Stepper, askConfirm } from '../components/ui.jsx';
import { useStore, actions } from '../store/store.js';
import { stockFor } from '../store/selectors.js';

export function KioskGrill() {
  const state = useStore();
  const g = state.grill || { prepared: 0, sold: 0 };
  const kvar = g.prepared - g.sold;
  const [inQty, setInQty] = useState(1);
  const [busy, setBusy] = useState(false);

  const meal = state.products.find(p => p.meal);
  const mealsPossible = meal ? stockFor(state, meal) : 0;

  async function guard(fn) {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } catch {
      /* reported by the store */
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="scroll" style={{ height: '100%', padding: '24px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: 'min(880px,100%)' }}>
        {/* hero: balance */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 22, background: 'linear-gradient(120deg,#1d4a2e,#2f7d49)', borderRadius: 26, padding: '22px 28px', color: '#fff', marginBottom: 20 }}>
          <div style={{ fontSize: 52 }}>🍔</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '1px', opacity: .8 }}>KVAR PÅ GRILLEN</div>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 44, lineHeight: 1 }}>{kvar} hamburgarmål</div>
            <div style={{ fontSize: 14, opacity: .85, fontWeight: 600, marginTop: 4 }}>{g.prepared} lagda på grillen · {g.sold} sålda i kassan</div>
          </div>
          {(g.prepared > 0 || g.sold > 0) && (
            <div style={{ textAlign: 'center', background: 'rgba(255,255,255,.12)', borderRadius: 18, padding: '12px 20px' }}>
              {kvar > 0 && <><div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 26 }}>+{kvar}</div><div style={{ fontSize: 12.5, fontWeight: 700, opacity: .85 }}>burgare över</div></>}
              {kvar === 0 && <><div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 26 }}>✓</div><div style={{ fontSize: 12.5, fontWeight: 700, opacity: .85 }}>jämnt räknat</div></>}
              {kvar < 0 && <><div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 26, color: '#ffcf8a' }}>{Math.abs(kvar)}</div><div style={{ fontSize: 12.5, fontWeight: 700, opacity: .85 }}>fler sålda än lagda</div></>}
            </div>
          )}
        </div>

        <div className="grid-2">
          {/* burgers put on the grill */}
          <div className="card" style={{ padding: '22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Lagt på grillen</div>
            <p style={{ margin: '0 0 16px', fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5, fontWeight: 600 }}>
              Räkna in burgarna när de läggs på grillen, så vet kassan hur många hamburgarmål som finns kvar.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Antal burgare</span>
              <Stepper value={inQty} onChange={v => setInQty(Math.max(1, v))} min={1} />
            </div>
            <button className="btn btn-orange btn-lg btn-block" disabled={busy}
              onClick={() => guard(async () => { await actions.addPrepared(inQty); setInQty(1); })}>
              <Icon name="plus" size={19} /> Lägg på grillen
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 14, borderTop: '1.5px solid var(--line-soft)' }}>
              <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 700 }}>Totalt lagt: <b style={{ color: 'var(--ink)' }}>{g.prepared} burgare</b></span>
              <button className="btn btn-sm btn-danger" onClick={() => {
                askConfirm('Räknarna för lagda och sålda hamburgarmål nollställs inför ny match.',
                  { title: 'Nollställ grillen?', confirmLabel: 'Nollställ' })
                  .then(ok => ok && guard(() => actions.resetGrill()));
              }}>Ny match · nollställ</button>
            </div>
          </div>

          {/* meals sold at the register — read-only here, the sale happens in kassan */}
          <div className="card" style={{ padding: '22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Sålda i kassan</div>
            <p style={{ margin: '0 0 16px', fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5, fontWeight: 600 }}>
              Hamburgarmålet slås in i kassan som vilken vara som helst — varje sålt mål plockar en burgare från grillen här.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: '#fbf6ec', borderRadius: 18, padding: '16px 20px' }}>
              <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 40, lineHeight: 1, color: 'var(--green-deep)' }}>{g.sold}</div>
              <div style={{ lineHeight: 1.25 }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>hamburgarmål sålda</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>sedan grillen nollställdes</div>
              </div>
            </div>
            {meal ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, paddingTop: 14, borderTop: '1.5px solid var(--line-soft)', fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>
                <span>Vara: <b style={{ color: 'var(--ink)' }}>{meal.emoji} {meal.name}</b></span>
                <span>Mål möjliga från lagret: <b style={{ color: 'var(--ink)' }}>{Number.isFinite(mealsPossible) ? mealsPossible : '∞'}</b></span>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16, paddingTop: 14, borderTop: '1.5px solid var(--line-soft)', fontSize: 13.5, fontWeight: 600, color: 'var(--orange-deep)', lineHeight: 1.45 }}>
                <Icon name="warn" size={18} />
                <span>Ingen vara är markerad som hamburgarmål — be admin bocka i <b>Hamburgarmål</b> på varan under Varor &amp; lager, annars räknas grillen inte av.</span>
              </div>
            )}
          </div>
        </div>

        {(g.prepared > 0 || g.sold > 0) && (
          <div className="card-flat" style={{ marginTop: 16, padding: '14px 18px', display: 'flex', gap: 11, alignItems: 'flex-start', borderLeft: '5px solid ' + (kvar < 0 ? 'var(--orange)' : 'var(--green)') }}>
            <Icon name="info" size={19} color={kvar < 0 ? 'var(--orange-deep)' : 'var(--green)'} />
            <div style={{ fontSize: 14, lineHeight: 1.5, fontWeight: 600, color: 'var(--ink-soft)' }}>
              {kvar > 0 && <>Det ligger <b>{kvar} burgare</b> kvar på grillen som inte sålts ännu — håll koll så de inte blir stående och hamnar i svinnet.</>}
              {kvar === 0 && <>Grillen går jämnt ut — lika många mål sålda som burgare lagda. 👌</>}
              {kvar < 0 && <>Ni har sålt <b>{Math.abs(kvar)} mål fler</b> än det lagts på grillen — lägg till burgarna här så stämmer räkningen igen.</>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
